import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_LINKS } from './constants/appLinks';

function Landing() {
  const navigate = useNavigate();

  const handleGetEarlyAccess = () => {
    // Currently links to waitlist, easy to swap to App Store later
    navigate(APP_LINKS.BETA_WAITLIST);
  };

  // Check if there are auth tokens in the URL hash (from email links)
  useEffect(() => {
    const hash = window.location.hash;

    // If we have auth tokens in the hash, redirect to auth callback
    if (hash && (hash.includes('access_token') || hash.includes('error'))) {
      console.log('Auth tokens detected on landing page, redirecting to /auth/callback');
      navigate('/auth/callback' + hash, { replace: true });
    }
  }, [navigate]);

  return (
    <div className="bg-slate-950 text-slate-300 antialiased selection:bg-cyan-500/30 selection:text-cyan-200">
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20"></div>
        {/* Top Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-cyan-500/10 blur-[120px] rounded-full mix-blend-screen"></div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 glass-panel">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-red-700 border border-red-500/30 group-hover:border-red-400/60 group-hover:shadow-[0_0_20px_rgba(230,57,70,0.5)] transition-all duration-300">
              <span className="text-xl font-bold text-white" style={{ fontFamily: "'Teko', 'Rajdhani', sans-serif" }}>R</span>
              {/* Lightning bolt */}
              <svg className="absolute w-4 h-5 opacity-70" viewBox="0 0 24 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 0 3px rgba(255,182,39,0.6))' }}>
                <path d="M14 0L0 22h10L8 40l16-24H14L14 0z" fill="#FFB627"/>
              </svg>
            </div>
            <div>
              <span className="text-xl font-semibold tracking-tight text-white group-hover:text-red-50 transition-colors" style={{ fontFamily: "'Teko', 'Rajdhani', sans-serif", letterSpacing: '-0.5px' }}>
                <span className="text-red-500">R</span>IVALIS
              </span>
              <p className="text-xs text-slate-500">AI-powered fitness competitions</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="hidden md:block text-base text-slate-400 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={handleGetEarlyAccess}
              className="px-6 py-2.5 rounded-xl bg-cyan-500 text-slate-950 text-base font-medium hover:bg-cyan-400 transition-all shadow-[0_0_20px_-5px_rgba(34,211,238,0.4)]"
            >
              Get Early Access
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* Hero Content */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950/30 border border-cyan-800/50 text-cyan-400 text-sm font-medium mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              AI-powered coaching available now
            </div>

            <h1 className="text-5xl md:text-7xl font-medium tracking-tight text-white mb-8 leading-[1.1]">
              Personalized <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400 text-glow">coaching.</span>
            </h1>

            <p className="text-xl text-slate-400 mb-10 leading-relaxed max-w-lg">
              Sync your Apple Watch, unlock AI-powered training plans, and see your progress in real time. Designed for athletes who want actionable data, not guesswork.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={handleGetEarlyAccess}
                className="px-8 py-4 rounded-xl bg-cyan-500 text-slate-950 text-lg font-medium hover:bg-cyan-400 transition-all duration-300 shadow-[0_0_30px_-5px_rgba(34,211,238,0.4)]"
                data-testid="landing-get-early-access"
              >
                Get Early Access
              </button>
              <button
                type="button"
                onClick={() => navigate('/onboarding')}
                className="px-8 py-4 rounded-xl bg-slate-900 border border-slate-700 text-white text-lg font-medium hover:bg-slate-800 transition-all duration-300"
                data-testid="landing-build-ai-plan"
              >
                Build My AI Plan
              </button>
            </div>

            <div className="mt-12 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 rounded-full bg-slate-800 border-2 border-slate-950"></div>
                <div className="w-8 h-8 rounded-full bg-slate-700 border-2 border-slate-950"></div>
                <div className="w-8 h-8 rounded-full bg-slate-600 border-2 border-slate-950"></div>
                <div className="w-8 h-8 rounded-full bg-cyan-500/20 border-2 border-slate-950 flex items-center justify-center text-cyan-400 text-xs font-medium">+1k</div>
              </div>
              <p>Athletes training today</p>
            </div>
          </div>

          {/* Hero Visual (Dashboard Preview) */}
          <div className="relative lg:h-[650px] flex justify-center lg:justify-end">
            {/* Dashboard Card */}
            <div className="relative w-full max-w-[420px] bg-slate-950 rounded-3xl border border-slate-800 shadow-2xl box-glow overflow-hidden z-20">
              {/* Screen Content */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/40 via-slate-950 to-slate-950">
                {/* Grid inside card */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(34,211,238,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(34,211,238,0.05)_1px,transparent_1px)] bg-[size:2rem_2rem]"></div>

                <div className="relative h-full flex flex-col p-8 pt-12">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-cyan-400">Today's Focus</p>
                      <h3 className="text-2xl font-semibold text-white mt-1">Strength Builder</h3>
                    </div>
                    <span className="text-3xl">💪</span>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-slate-900/60 backdrop-blur border border-cyan-500/20 rounded-xl px-4 py-4">
                      <p className="text-xs font-semibold text-cyan-400 uppercase tracking-wide">Streak</p>
                      <p className="text-2xl font-bold text-white mt-2">3 days</p>
                    </div>
                    <div className="bg-slate-900/60 backdrop-blur border border-emerald-500/20 rounded-xl px-4 py-4">
                      <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wide">Progress</p>
                      <p className="text-2xl font-bold text-white mt-2">82%</p>
                    </div>
                  </div>

                  {/* AI Tip Card */}
                  <div className="bg-slate-900/80 backdrop-blur border border-slate-700 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                      <span className="text-xs font-semibold text-cyan-400 tracking-wide">AI TIP</span>
                    </div>
                    <p className="text-sm text-slate-300">"Increase tempo on last set for optimal hypertrophy"</p>
                  </div>

                  {/* Chart Visualization */}
                  <div className="flex-1 relative rounded-xl bg-slate-900/40 border border-slate-800 p-4 overflow-hidden">
                    <div className="absolute inset-0 flex items-end px-4 pb-4 space-x-2">
                      <div className="w-full bg-cyan-500/20 rounded-t-sm h-[40%]"></div>
                      <div className="w-full bg-cyan-500/30 rounded-t-sm h-[65%]"></div>
                      <div className="w-full bg-cyan-500/40 rounded-t-sm h-[50%]"></div>
                      <div className="w-full bg-cyan-500/70 rounded-t-sm h-[85%] shadow-[0_0_20px_rgba(34,211,238,0.3)]"></div>
                      <div className="w-full bg-cyan-500/50 rounded-t-sm h-[60%]"></div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    type="button"
                    onClick={() => navigate('/onboarding')}
                    className="mt-6 w-full bg-gradient-to-r from-cyan-600 to-cyan-500 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg shadow-cyan-900/50 hover:shadow-cyan-900/70"
                  >
                    Build My AI Plan
                  </button>
                </div>
              </div>
            </div>
            {/* Background decorative glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[680px] bg-gradient-to-b from-cyan-500/20 to-transparent rounded-[3.5rem] blur-2xl -z-10"></div>
          </div>
        </div>
      </main>

      {/* Features Grid */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-white mb-6">Built for the data-driven athlete.</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">Standard trackers just log data. We transform your metrics into actionable coaching that adapts to your progress.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Feature 1 */}
            <div className="group p-8 rounded-2xl bg-slate-900/40 border border-slate-800 hover:border-cyan-500/50 transition-all duration-300 hover:bg-slate-900/60 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-slate-700 group-hover:border-cyan-500/30">
                <span className="text-2xl">🍎</span>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">Connected Coaching</h3>
              <p className="text-lg text-slate-400 leading-relaxed">Built around your Apple Health metrics, our plans update automatically as your body adapts.</p>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 rounded-2xl bg-slate-900/40 border border-slate-800 hover:border-cyan-500/50 transition-all duration-300 hover:bg-slate-900/60 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-slate-700 group-hover:border-cyan-500/30">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">AI Insights</h3>
              <p className="text-lg text-slate-400 leading-relaxed">Translate raw data into clear next steps, from nutrition adjustments to recovery cues.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Feature Section */}
      <section className="py-24 border-y border-white/5 bg-slate-900/20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="order-2 md:order-1">
            {/* Abstract Dashboard Visualization */}
            <div className="relative w-full aspect-square md:aspect-[4/3] rounded-2xl bg-slate-950 border border-slate-800 p-6 shadow-2xl overflow-hidden">
              {/* Grid BG */}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-30"></div>

              {/* Chart Line */}
              <div className="absolute bottom-0 left-0 right-0 h-2/3 flex items-end px-6 space-x-2">
                <div className="w-full bg-cyan-500/10 rounded-t-sm h-[40%] animate-pulse"></div>
                <div className="w-full bg-cyan-500/20 rounded-t-sm h-[65%]"></div>
                <div className="w-full bg-cyan-500/40 rounded-t-sm h-[50%]"></div>
                <div className="w-full bg-cyan-500/60 rounded-t-sm h-[85%] shadow-[0_0_20px_rgba(34,211,238,0.5)]"></div>
                <div className="w-full bg-cyan-500/30 rounded-t-sm h-[60%]"></div>
              </div>

              {/* Floating UI Card */}
              <div className="absolute top-10 right-10 w-48 bg-slate-900/90 backdrop-blur border border-cyan-500/30 rounded-lg p-4 shadow-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-xs font-semibold text-white tracking-wide">LIVE SESSION</span>
                </div>
                <div className="flex justify-between items-end">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase">Heart Rate</span>
                    <div className="text-xl font-bold text-white">142 <span className="text-xs font-normal text-slate-500">bpm</span></div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-400 uppercase">Target</span>
                    <div className="text-xl font-bold text-cyan-400">150</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-white mb-6">Designed for performance tracking.</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="mt-1 w-6 h-6 rounded-full bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <span className="text-cyan-400 text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-white">Real-Time Analytics</h4>
                  <p className="text-lg text-slate-400 mt-1">Track your metrics as they happen during workouts with live updates from your Apple Watch.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-1 w-6 h-6 rounded-full bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <span className="text-cyan-400 text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-white">Progress Visualization</h4>
                  <p className="text-lg text-slate-400 mt-1">Beautiful charts and graphs that make it easy to see trends and celebrate improvements.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="mt-1 w-6 h-6 rounded-full bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <span className="text-cyan-400 text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-white">Adaptive Planning</h4>
                  <p className="text-lg text-slate-400 mt-1">Your training plan adjusts automatically based on recovery, performance, and progress.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-cyan-900/5"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-cyan-500/20 blur-[100px] rounded-full"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-medium tracking-tight text-white mb-6">Ready to transform your training?</h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">Join thousands of athletes who have unlocked their potential with data-driven coaching.</p>

          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={handleGetEarlyAccess}
                className="px-8 py-4 rounded-xl bg-cyan-500 text-slate-950 text-lg font-medium hover:bg-cyan-400 transition-all duration-300 shadow-[0_0_30px_-5px_rgba(34,211,238,0.4)]"
              >
                Get Early Access
              </button>
              <button
                type="button"
                onClick={() => navigate('/onboarding')}
                className="px-8 py-4 rounded-xl bg-slate-900 border border-slate-700 text-white text-lg font-medium hover:bg-slate-800 transition-all duration-300"
              >
                Build My AI Plan
              </button>
            </div>
            <p className="text-sm text-slate-500">
              Already a member?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="relative w-8 h-8 rounded-lg bg-gradient-to-br from-red-600 to-red-700 border border-red-500/30 flex items-center justify-center">
              <span className="text-sm font-bold text-white" style={{ fontFamily: "'Teko', 'Rajdhani', sans-serif" }}>R</span>
              {/* Lightning bolt */}
              <svg className="absolute w-3 h-4 opacity-60" viewBox="0 0 24 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 0L0 22h10L8 40l16-24H14L14 0z" fill="#FFB627"/>
              </svg>
            </div>
            <span className="text-lg font-semibold tracking-tight text-white" style={{ fontFamily: "'Teko', 'Rajdhani', sans-serif", letterSpacing: '-0.5px' }}>
              <span className="text-red-500">R</span>IVALIS
            </span>
          </div>

          <div className="flex gap-8 text-sm text-slate-500">
            <a href="#" className="hover:text-cyan-400 transition-colors">Privacy</a>
            <a href="#" className="hover:text-cyan-400 transition-colors">Terms</a>
            <a href="#" className="hover:text-cyan-400 transition-colors">Support</a>
            <button
              onClick={() => navigate('/login')}
              className="hover:text-cyan-400 transition-colors"
            >
              Sign In
            </button>
          </div>

          <div className="text-sm text-slate-600">
            © 2024 Rivalis. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
